import { jwtDecode } from 'jwt-decode';

const LOGIN_TOKEN_QUERY_KEYS = [
  'first_login_success_token',
  'reset_success_token',
];

interface LoginIdentity {
  client?: string;
  client_id?: string | number;
  client_name?: string;
  email?: string;
  user_id?: string | number;
}

interface LoginTokenClaims {
  identity?: LoginIdentity;
}

interface PasswordResetResponse {
  msg?: string;
  token?: string;
  first_login?: boolean;
}

export interface LoginHandoffFeatureFlags {
  searchAsHome: boolean;
}

export function readLoginToken(tokenOrUrl: string) {
  if (typeof tokenOrUrl !== 'string' || tokenOrUrl.length === 0) return '';
  try {
    const url = new URL(tokenOrUrl);
    for (const key of LOGIN_TOKEN_QUERY_KEYS) {
      const value = url.searchParams.get(key);
      if (value) return value;
    }
  } catch {
    return tokenOrUrl;
  }
  return tokenOrUrl;
}

export function resolvePasswordResetHandoff(response?: PasswordResetResponse) {
  if (response?.token) {
    return {
      token: response.token,
      isFirstLogin: Boolean(response.first_login),
    };
  }

  const redirectUrl = typeof response?.msg === 'string' ? response.msg : '';
  if (!redirectUrl) return {};

  try {
    const url = new URL(redirectUrl);
    if (url.searchParams.has('first_login_success_token')) {
      return { redirectUrl, isFirstLogin: true };
    }
    if (url.searchParams.has('reset_success_token')) {
      return { redirectUrl, isFirstLogin: false };
    }
  } catch {
    return { redirectUrl };
  }

  return { redirectUrl };
}

export function workspacePathAfterLogin({
  client,
  isFirstLogin,
  featureFlags,
}: {
  client?: string;
  isFirstLogin: boolean;
  featureFlags: LoginHandoffFeatureFlags;
}) {
  if (isFirstLogin && featureFlags.searchAsHome) {
    return `/${client}/search?welcomeMessage=true`;
  }
  if (isFirstLogin) return `/${client}/?welcomeMessage=true`;
  return `/${client}/`;
}

export const getDecodedUserInfoFromLoginToken = (tokenOrUrl: string) => {
  const decodedToken = jwtDecode<LoginTokenClaims>(readLoginToken(tokenOrUrl));

  return {
    client: decodedToken?.identity?.client ?? '',
    client_id: decodedToken?.identity?.client_id ?? '',
    client_name: decodedToken?.identity?.client_name ?? '',
    email: decodedToken?.identity?.email ?? '',
    user_id: decodedToken?.identity?.user_id ?? '',
  };
};

export const getDecodedUserInfoFromToken = (accessToken: string) => {
  const decodedToken = jwtDecode<LoginTokenClaims>(accessToken);

  return {
    client_id: decodedToken?.identity?.client_id ?? '',
    client_name: decodedToken?.identity?.client_name ?? '',
    user_id: decodedToken?.identity?.user_id ?? '',
  };
};

export const testIsValidBase64String = (value: string) => {
  if (!value) return false;
  return /^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)?$/.test(
    value,
  );
};
